import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  FileText, Headphones, Video, Wrench, Inbox, Users, LogOut, ExternalLink,
  Users2, ListMusic, Radio, Mail, PoundSterling, Newspaper, Settings,
  DoorOpen, Info, BookOpen, MessageCircle, BarChart3, ChevronRight, ArrowLeft, Home,
  Trophy, Megaphone, Activity, Scale, MapPin, Clock, LayoutTemplate, Calculator,
  FileCheck2, CalendarDays, Briefcase, Share2, Search, Menu, ShieldCheck, Tag,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { adminApi } from "../../lib/api";
import CrudManager from "../../components/admin/CrudManager";
import EnquiriesManager from "../../components/admin/EnquiriesManager";
import UsersManager from "../../components/admin/UsersManager";
import StaffManager from "../../components/admin/StaffManager";
import SeriesManager from "../../components/admin/SeriesManager";
import PagesManager from "../../components/admin/PagesManager";
import PodcastLinksManager from "../../components/admin/PodcastLinksManager";
import QuotesManager from "../../components/admin/QuotesManager";
import JobApplicationsManager from "../../components/admin/JobApplicationsManager";
import CareersManager from "../../components/admin/CareersManager";
import AnalyticsManager from "../../components/admin/AnalyticsManager";
import DashboardSummary from "../../components/admin/DashboardSummary";
import AnalyticsReset from "../../components/admin/AnalyticsReset";
import ServicesManager from "../../components/admin/ServicesManager";
import MeetingRoomManager from "../../components/admin/MeetingRoomManager";
import AboutManager from "../../components/admin/AboutManager";
import GuidesManager from "../../components/admin/GuidesManager";
import ChatFaqsManager from "../../components/admin/ChatFaqsManager";
import MetricoolManager from "../../components/admin/MetricoolManager";
import SearchConsoleManager from "../../components/admin/SearchConsoleManager";
import SeoAuditPanel from "../../components/admin/SeoAuditPanel";
import SeoControlsPanel from "../../components/admin/SeoControlsPanel";
import AeoDashboard from "../../components/admin/AeoDashboard";
import TrackingSetupPanel from "../../components/admin/TrackingSetupPanel";
import CassiePromptsManager from "../../components/admin/CassiePromptsManager";
import AskCassieManager from "../../components/admin/AskCassieManager";
import CassieQuestionsManager from "../../components/admin/CassieQuestionsManager";
import SubscribersManager from "../../components/admin/SubscribersManager";
import HomepageManager from "../../components/admin/HomepageManager";
import FooterManager from "../../components/admin/FooterManager";
import NavigationManager from "../../components/admin/NavigationManager";
import RetentionManager from "../../components/admin/RetentionManager";
import CaseStudiesManager from "../../components/admin/CaseStudiesManager";
import PromoManager from "../../components/admin/PromoManager";
import ShareCardsManager from "../../components/admin/ShareCardsManager";
import LeadSourceManager from "../../components/admin/LeadSourceManager";
import LeadSourceReport from "../../components/admin/LeadSourceReport";
import HealthCheckManager from "../../components/admin/HealthCheckManager";
import NotificationRecipientsManager from "../../components/admin/NotificationRecipientsManager";
import TownTestimonialsManager from "../../components/admin/TownTestimonialsManager";
import { GoogleBusinessConnect, GbpPostButton } from "../../components/admin/GoogleBusinessManager";
import AssessmentManager from "../../components/admin/AssessmentManager";
import BlogManager from "../../components/admin/BlogManager";
import ContentTemplatesManager from "../../components/admin/ContentTemplatesManager";
import CalculatorManager from "../../components/admin/CalculatorManager";
import MtdManager from "../../components/admin/MtdManager";
import TaxDatesManager from "../../components/admin/TaxDatesManager";
import XeroPricingManager from "../../components/admin/XeroPricingManager";
import PackageBuilderManager from "../../components/admin/PackageBuilderManager";
import GooglePerformance from "../../components/admin/google/GooglePerformance";
import { ClipboardCheck } from "lucide-react";
import logoLight from "../../assets/cass-logo-light.png";

const SECTIONS = {
  analytics: { label: "Analytics", icon: BarChart3, desc: "Leads, trends & performance overview", render: () => (<><AnalyticsManager /><DashboardSummary /><AnalyticsReset /></>) },
  enquiries: { label: "Enquiries", icon: Inbox, desc: "Contact form submissions", render: () => <EnquiriesManager /> },
  quotes: { label: "Quote Requests", icon: FileText, desc: "Pricing enquiries", render: () => <QuotesManager /> },
  jobApplications: { label: "CV Applications", icon: Briefcase, desc: "Careers page CV submissions", render: () => <JobApplicationsManager /> },
  careers: { label: "Careers", icon: Briefcase, desc: "Vacancies & applicant emails", render: () => <CareersManager /> },
  staff: { label: "Team / Staff", icon: Users2, desc: "Manage team members", render: () => <StaffManager /> },
  blog: {
    label: "Blog Post", icon: FileText, desc: "Articles & news",
    render: () => <BlogManager />,
  },
  podcastSeries: { label: "Podcast Series", icon: ListMusic, desc: "Series, episodes & uploads", render: () => <SeriesManager /> },
  podcastLinks: { label: "Podcast Links", icon: Radio, desc: "Streaming platform links", render: () => <PodcastLinksManager /> },
  subscribers: { label: "Podcast Subscribers", icon: Mail, desc: "Email list", render: () => <SubscribersManager /> },
  videoTestimonials: {
    label: "Video Testimonials", icon: Video, desc: "Client video reviews",
    render: () => (
      <CrudManager
        label="Video Testimonial"
        api={adminApi.videoTestimonials}
        makeEmpty={() => ({ title: "", youtubeId: "", thumbnail: "" })}
        renderRow={(i) => ({ title: i.title, subtitle: `YouTube: ${i.youtubeId}`, image: i.thumbnail })}
        fields={[
          { name: "title", label: "Title", type: "text" },
          { name: "youtubeId", label: "YouTube link or Video ID", type: "text", hint: "Paste the full YouTube URL (or just the ID) — the ID and thumbnail are detected automatically." },
          { name: "thumbnail", label: "Thumbnail Image URL", type: "text", hint: "Leave blank to auto-use the YouTube thumbnail." },
        ]}
      />
    ),
  },
  services: { label: "Services", icon: Wrench, desc: "Service offerings", render: () => <ServicesManager /> },
  users: { label: "Admin Users", icon: Users, desc: "Panel access & logins", render: () => <UsersManager /> },
  pages: { label: "Pages", icon: Newspaper, desc: "Static content pages", render: () => <PagesManager /> },
  xeroPricing: { label: "Xero Prices", icon: PoundSterling, desc: "Xero plan prices shown on the Xero Subscription page", render: () => <XeroPricingManager /> },
  packageBuilder: { label: "Pricing Page (What Would CASS Cost?)", icon: PoundSterling, desc: "The pricing journey at /pricing — visibility, all page copy, insights & journeys", render: () => <PackageBuilderManager /> },
  meetingRoom: { label: "Meeting Room Hire", icon: DoorOpen, desc: "Room hire details", render: () => <MeetingRoomManager /> },
  about: { label: "About Page", icon: Info, desc: "About page content", render: () => <AboutManager /> },
  guides: { label: "Guides / Downloads", icon: BookOpen, desc: "Free lead-magnet guides", render: () => <GuidesManager /> },
  caseStudies: { label: "Success Stories", icon: Trophy, desc: "Client case studies", render: () => <CaseStudiesManager /> },
  contentTemplates: { label: "Content Templates", icon: LayoutTemplate, desc: "Reusable blog & success-story starter templates", render: () => <ContentTemplatesManager /> },
  promo: { label: "Homepage Promo", icon: Megaphone, desc: "Homepage promo popup", render: () => <PromoManager /> },
  healthCheck: { label: "Health Check (legacy)", icon: Activity, desc: "Original Business Health Check submissions (historical)", render: () => <HealthCheckManager /> },
  assessments: { label: "Assessment Tools", icon: ClipboardCheck, desc: "Business assessment tools — submissions, questions & scoring", render: () => <AssessmentManager /> },
  numbersCalculator: { label: "Key Business Numbers Calculator", icon: Calculator, desc: "Key Business Numbers Calculator — content, metrics, leads & analytics", render: () => <CalculatorManager /> },
  mtdChecker: { label: "MTD Checker", icon: FileCheck2, desc: "MTD for Income Tax checker — copy, thresholds, AI toggle & leads", render: () => <MtdManager /> },
  taxDates: { label: "Key Tax Dates", icon: CalendarDays, desc: "Deadline hub content & reminder sign-ups", render: () => <TaxDatesManager /> },
  chatFaqs: { label: "Cassie (Chatbot)", icon: MessageCircle, desc: "AI assistant knowledge & FAQs", render: () => <ChatFaqsManager /> },
  cassieQuestions: { label: "Cassie Questions", icon: MessageCircle, desc: "Everything visitors have asked Cassie", render: () => <CassieQuestionsManager /> },
  cassiePrompts: { label: "Suggested Questions", icon: MessageCircle, desc: "Editable per-page chat prompts", render: () => <CassiePromptsManager /> },
  askCassie: { label: "Ask Cassie (Knowledge Centre)", icon: MessageCircle, desc: "The answer library at /ask-cassie — crawler-readable pages & review status", render: () => <AskCassieManager /> },
  metricool: { label: "Social Posting (Metricool)", icon: Share2, desc: "Send blog & custom posts to Metricool", render: () => <MetricoolManager /> },
  googlePerformance: {
    label: "Google Performance & Visibility", icon: BarChart3,
    desc: "One place for traffic, search, ads, enquiries, SEO and AI visibility, and the health of every connection",
    render: () => <GooglePerformance />,
  },
  searchConsole: { label: "Search Console (SEO)", icon: Search, desc: "Google Search Console performance & URL index status — reporting only", render: () => <SearchConsoleManager /> },
  seoAudit: { label: "SEO Audit", icon: ShieldCheck, desc: "What a crawler actually receives on every page, with priorities and a baseline to measure against", render: () => <SeoAuditPanel /> },
  seoControls: { label: "Page SEO Controls", icon: FileText, desc: "Title, description, canonical, index switch and related links for any URL", render: () => <SeoControlsPanel /> },
  aeoDashboard: { label: "Search & AI Visibility", icon: BarChart3, desc: "Non-branded search, AI referrals, the monthly AI recommendation log and resulting enquiries", render: () => <AeoDashboard /> },
  trackingSetup: { label: "Tracking & Tags", icon: Tag, desc: "What GA4 and Tag Manager are told, which events count as conversions, and what each number can't prove", render: () => <TrackingSetupPanel /> },
  homepage: { label: "Homepage", icon: Home, desc: "Edit homepage sections & content", render: () => <HomepageManager /> },
  retention: { label: "Data retention", icon: ShieldCheck, desc: "How long searches and Cassie transcripts are kept, and delete records on demand", render: () => <RetentionManager /> },
  navigation: { label: "Navigation (Menus)", icon: Menu, desc: "Where each page sits in the header menu — order, labels, dropdowns & visibility", render: () => <NavigationManager /> },
  footer: { label: "Footer", icon: Newspaper, desc: "Edit site-wide footer content", render: () => <FooterManager /> },
  notifications: { label: "Email Recipients", icon: Mail, desc: "Who receives which notification emails", render: () => <NotificationRecipientsManager /> },
  townPages: { label: "Town Pages", icon: MapPin, desc: "Per-town local page testimonials", render: () => <TownTestimonialsManager /> },
  leadSources: { label: "Lead Source Management", icon: Megaphone, desc: "The \"How did you hear about us?\" options shown on every form", render: () => <LeadSourceManager /> },
  leadSourceReport: { label: "Lead Source Report", icon: BarChart3, desc: "Which channels, partners and referrals actually produce enquiries", render: () => <LeadSourceReport /> },
  shareCards: { label: "Share Cards (Social Previews)", icon: Share2, desc: "What LinkedIn, Facebook & WhatsApp show when a CASS link is posted", render: () => <ShareCardsManager /> },
};

// Dashboard tiles: single "item" tiles open a section directly; "group" tiles open a sub-grid.
const DASHBOARD = [
  { type: "group", key: "inbox", label: "Inbox", icon: Inbox, desc: "Enquiries, quotes & CV applications", children: ["enquiries", "quotes", "jobApplications"] },
  { type: "group", key: "content", label: "Content", icon: Newspaper, desc: "Homepage, blog, videos, stories, town pages, guides & rooms", children: ["homepage", "promo", "blog", "videoTestimonials", "caseStudies", "contentTemplates", "pages", "about", "guides", "assessments", "numbersCalculator", "mtdChecker", "taxDates", "healthCheck", "meetingRoom", "townPages"] },
  { type: "group", key: "podcast", label: "Podcast", icon: Headphones, desc: "Series, episodes, links & subscribers", children: ["podcastSeries", "podcastLinks", "subscribers"] },
  { type: "group", key: "servicesPricing", label: "Services & Pricing", icon: Wrench, desc: "Manage services & pricing", children: ["services", "packageBuilder", "xeroPricing"] },
  { type: "item", key: "staff" },
  { type: "item", key: "careers" },
  { type: "group", key: "leadSource", label: "Lead Sources", icon: Megaphone, desc: "How people say they heard about CASS, and the report", children: ["leadSourceReport", "leadSources"] },
  { type: "group", key: "cassie", label: "Cassie (Chatbot)", icon: MessageCircle, desc: "AI assistant knowledge & suggested questions", children: ["chatFaqs", "cassieQuestions", "cassiePrompts", "askCassie"] },
  { type: "item", key: "metricool" },
  { type: "item", key: "shareCards" },
  { type: "item", key: "googlePerformance" },
  // Editing SEO settings is website management, not reporting, so it stays put.
  { type: "group", key: "seoSettings", label: "Website SEO Settings", icon: Search, desc: "Page titles, descriptions, canonicals and the index switch", children: ["seoControls"] },
  { type: "link", key: "legalCashiering", label: "Legal Cashiering", icon: Scale, desc: "Manage the Legal Cashiering division (own panel)", path: "/admin/legal-cashiering" },
  { type: "group", key: "settings", label: "Settings", icon: Settings, desc: "Menus, footer, email recipients, data retention, admin users & access", children: ["navigation", "footer", "notifications", "retention", "users"] },
];

function CountBadge({ n }) {
  if (!n) return null;
  return (
    <span className="min-w-[22px] h-5 px-1.5 rounded-full bg-orange text-white text-[11px] font-bold flex items-center justify-center leading-none">
      {n > 99 ? "99+" : n}
    </span>
  );
}

export default function AdminApp() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // Navigation is driven by the URL so the browser Back button walks Section -> Group -> Dashboard.
  const rawGroup = searchParams.get("group");
  const rawSection = searchParams.get("section");
  const group = rawGroup && DASHBOARD.some((d) => d.key === rawGroup && d.type === "group") ? rawGroup : null;
  const section = rawSection && SECTIONS[rawSection] ? rawSection : null;
  const [counts, setCounts] = useState({ enquiries: 0, quotes: 0, jobApplications: 0 });

  const setSection = (key) => {
    const params = {};
    if (group) params.group = group;
    if (key) params.section = key;
    setSearchParams(params);
  };

  const handleLogout = () => { localStorage.removeItem("admin-last-view"); logout(); navigate("/admin/login"); };

  // Sticky tabs: restore last-open view when landing on a bare /admin (fresh login / bookmark).
  const didRestore = React.useRef(false);
  useEffect(() => {
    if (didRestore.current) return;
    didRestore.current = true;
    if (searchParams.get("group") || searchParams.get("section")) return;
    try {
      const saved = JSON.parse(localStorage.getItem("admin-last-view") || "null");
      if (saved && (saved.group || saved.section)) {
        const next = {};
        if (saved.group && DASHBOARD.some((d) => d.key === saved.group && d.type === "group")) next.group = saved.group;
        if (saved.section && SECTIONS[saved.section]) next.section = saved.section;
        if (next.group || next.section) setSearchParams(next, { replace: true });
      }
    } catch { /* ignore corrupt storage */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist the current view so a later bare /admin visit can restore it.
  useEffect(() => {
    if (!didRestore.current) return;
    localStorage.setItem("admin-last-view", JSON.stringify({ group, section }));
  }, [group, section]);

  const refreshCounts = React.useCallback(() => {
    Promise.all([
      adminApi.enquiries.list().catch(() => []),
      adminApi.quotes.list().catch(() => []),
      adminApi.jobApplications.list().catch(() => []),
    ]).then(([e, q, j]) => setCounts({
      enquiries: e.filter((x) => !x.dealt_with).length,
      quotes: q.filter((x) => !x.dealt_with).length,
      jobApplications: j.filter((x) => (x.status || "New") === "New").length,
    }));
  }, []);

  useEffect(() => { refreshCounts(); }, [section, group, refreshCounts]);
  useEffect(() => {
    window.addEventListener("inbox-updated", refreshCounts);
    return () => window.removeEventListener("inbox-updated", refreshCounts);
  }, [refreshCounts]);

  const inboxTotal = counts.enquiries + counts.quotes + counts.jobApplications;
  const childCount = (k) => (k === "enquiries" ? counts.enquiries : k === "quotes" ? counts.quotes : k === "jobApplications" ? counts.jobApplications : 0);
  const groupBadge = (g) => (g.key === "inbox" ? inboxTotal : 0);

  const goHome = () => { setSearchParams({}); };
  const goBack = () => {
    if (section) { setSearchParams(group ? { group } : {}); }
    else if (group) { setSearchParams({}); }
  };

  const openTile = (entry) => {
    if (entry.type === "link") { navigate(entry.path); return; }
    if (entry.type === "item") { setSearchParams({ section: entry.key }); }
    else { setSearchParams({ group: entry.key }); }
  };

  const activeGroup = group ? DASHBOARD.find((d) => d.key === group) : null;

  // Breadcrumb pieces
  const crumbs = [{ label: "Dashboard", onClick: goHome }];
  if (activeGroup) crumbs.push({ label: activeGroup.label, onClick: () => setSection(null) });
  if (section) crumbs.push({ label: SECTIONS[section].label, onClick: null });

  const Tile = ({ icon: Icon, label, desc, badge, onClick, testid }) => (
    <button
      data-testid={testid}
      onClick={onClick}
      className="group text-left bg-white rounded-2xl border border-slate-200 p-6 shadow-sm hover:shadow-lg hover:border-orange/40 hover:-translate-y-0.5 transition-all duration-200 flex flex-col"
    >
      <div className="flex items-start justify-between mb-4">
        <div className="w-12 h-12 rounded-xl bg-navy/5 text-navy flex items-center justify-center group-hover:bg-orange group-hover:text-white transition-colors">
          <Icon size={22} />
        </div>
        <div className="flex items-center gap-2">
          {badge > 0 && <CountBadge n={badge} />}
          <ChevronRight size={18} className="text-slate-300 group-hover:text-orange group-hover:translate-x-0.5 transition-all" />
        </div>
      </div>
      <p className="font-display font-extrabold text-lg text-navy leading-tight">{label}</p>
      <p className="text-sm text-slate-400 mt-1">{desc}</p>
    </button>
  );

  const renderGrid = () => {
    // Home grid
    if (!activeGroup) {
      return (
        <div>
          <div className="flex items-center gap-3 mb-1">
            <span className="h-7 w-1.5 rounded-full bg-orange" />
            <h1 className="font-display font-extrabold text-2xl md:text-3xl text-navy">Admin Dashboard</h1>
          </div>
          <p className="text-sm text-slate-500 mb-8 pl-4">Welcome back{user?.name ? `, ${user.name.split(" ")[0]}` : ""} — choose an area to manage.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {DASHBOARD.map((entry) => {
              const meta = entry.type === "item" ? SECTIONS[entry.key] : entry;
              return (
                <Tile
                  key={entry.key}
                  testid={`tile-${entry.key}`}
                  icon={meta.icon}
                  label={meta.label}
                  desc={meta.desc}
                  badge={entry.type === "group" ? groupBadge(entry) : 0}
                  onClick={() => openTile(entry)}
                />
              );
            })}
          </div>
        </div>
      );
    }
    // Group sub-grid
    return (
      <div>
        <div className="flex items-center gap-3 mb-1">
          <span className="h-7 w-1.5 rounded-full bg-orange" />
          <h1 className="font-display font-extrabold text-2xl md:text-3xl text-navy">{activeGroup.label}</h1>
        </div>
        <p className="text-sm text-slate-500 mb-8 pl-4">{activeGroup.desc}</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {activeGroup.children.map((ck) => {
            const meta = SECTIONS[ck];
            return (
              <Tile
                key={ck}
                testid={`tile-${ck}`}
                icon={meta.icon}
                label={meta.label}
                desc={meta.desc}
                badge={childCount(ck)}
                onClick={() => setSection(ck)}
              />
            );
          })}
        </div>
      </div>
    );
  };

  const showBack = !!(section || group);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Top bar */}
      <header className="sticky top-0 z-40 bg-navy">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-4 md:px-8 h-16">
          <button data-testid="admin-logo-home" onClick={goHome} className="flex items-center gap-2.5">
            <img loading="lazy" decoding="async" src={logoLight} alt="CASS" className="h-9 w-auto" />
            <span className="text-xs text-slate-400 hidden sm:inline">Admin Panel</span>
            {inboxTotal > 0 && <CountBadge n={inboxTotal} />}
          </button>
          <div className="flex items-center gap-1 sm:gap-2">
            <a data-testid="admin-view-site" href="/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold text-slate-300 hover:bg-white/10 transition-colors">
              <ExternalLink size={16} /> <span className="hidden sm:inline">View website</span>
            </a>
            <button data-testid="admin-logout" onClick={handleLogout} className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold text-slate-300 hover:bg-white/10 transition-colors">
              <LogOut size={16} /> <span className="hidden sm:inline">Log out</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 md:px-8 py-8">
        {/* Breadcrumb + back */}
        <div className="flex items-center gap-3 mb-6 flex-wrap">
          {showBack && (
            <button data-testid="admin-back" onClick={goBack} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-slate-200 text-sm font-semibold text-navy hover:border-orange/40 hover:text-orange transition-colors">
              <ArrowLeft size={15} /> Back
            </button>
          )}
          <nav data-testid="admin-breadcrumb" className="flex items-center gap-1.5 text-sm text-slate-400">
            {crumbs.map((c, i) => (
              <span key={i} className="flex items-center gap-1.5">
                {i === 0 && <Home size={14} className="text-slate-400" />}
                {c.onClick ? (
                  <button onClick={c.onClick} className="font-semibold text-slate-500 hover:text-orange transition-colors">{c.label}</button>
                ) : (
                  <span className="font-semibold text-navy">{c.label}</span>
                )}
                {i < crumbs.length - 1 && <ChevronRight size={13} className="text-slate-300" />}
              </span>
            ))}
          </nav>
        </div>

        {section ? (
          <div data-testid={`section-${section}`}>{SECTIONS[section].render()}</div>
        ) : (
          renderGrid()
        )}
      </main>
    </div>
  );
}
